import { useState } from "react"

import useBatteryConcertApplication from "~/api/hooks/useBatteryConcertApplication"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalFinancing from "~/api/hooks/useBatteryProposalFinancing"

import { ConcertBatteryApplicationForm } from "./ConcertBatteryApplicationForm"
import { ConcertBatteryApplicationStatus } from "./ConcertBatteryApplicationStatus"
import { useBatteryConcertApplicationNotifications } from "./use-battery-concert-application-notifications"
import type { BatteryFinancingStatusExperienceProps } from "../financing-status-experiences"

/**
 * Concert status plus the corrected-form retry a confirmed provider
 * rejection permits. The retry reuses the Proposal's customer and the
 * current Quote; the server refuses it once an application was accepted.
 */
export function ConcertBatteryApplicationPanel({
  proposalId,
}: {
  proposalId: string
}): JSX.Element {
  const [retrying, setRetrying] = useState(false)
  useBatteryConcertApplicationNotifications(proposalId)
  const proposalQuery = useBatteryProposal(proposalId)
  const financingQuery = useBatteryProposalFinancing(proposalId)
  const statusQuery = useBatteryConcertApplication(proposalId)
  const proposal = proposalQuery.data
  const quoteId = financingQuery.data?.quote?.id ?? null
  const applicants = statusQuery.data?.application?.applicants ?? []
  const primary = applicants.find((applicant) => applicant.isPrimary) ?? null

  return (
    <>
      <ConcertBatteryApplicationStatus
        proposalId={proposalId}
        onRetry={
          proposal != null && quoteId != null
            ? () => setRetrying(true)
            : undefined
        }
      />
      {retrying && proposal != null && quoteId != null ? (
        <ConcertBatteryApplicationForm
          proposalId={proposalId}
          quoteId={quoteId}
          prefill={{
            firstName: primary?.firstName ?? proposal.homeowner?.firstName ?? ``,
            lastName: primary?.lastName ?? proposal.homeowner?.lastName ?? ``,
            email: primary?.email ?? proposal.homeowner?.email ?? ``,
            phone: primary?.phone ?? proposal.homeowner?.phone ?? ``,
            street: proposal.address.street,
            city: proposal.address.city,
            state: proposal.address.state,
            zip: proposal.address.postal,
          }}
          onClose={() => setRetrying(false)}
          onSubmitted={() => setRetrying(false)}
        />
      ) : null}
    </>
  )
}

/** Registry adapter for the routed financing-status drawer. */
export const ConcertBatteryFinancingStatus = ({
  proposalId,
}: BatteryFinancingStatusExperienceProps): JSX.Element => (
  <ConcertBatteryApplicationPanel proposalId={proposalId} />
)
